import AppButton from '@nxsys/components/AppButton';
import * as Icons from '@nxsys/components/AppIconSvg';
import AppCard from '@nxsys/components/AppCard';
import AppPageTitle from '@nxsys/components/AppPageTitle';
import AppsContainer from '@nxsys/components/AppsContainer';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch, AppState } from '@nxsys/store';
import { exportActions } from '@nxsys/store/slices/export/reducer/exportSlice';
import {
  ExportCompanyParams,
  GetExportListParams,
} from '@nxsys/store/slices/export/types';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { getColumns } from './columns';
import ExportCompanyModal from '../components/ExportCompanyModal';
import { toNumber } from 'lodash';
import { FILE_TYPE_VALUE } from '@nxsys/utils';
import { showMessage } from '@nxsys/utils/message';

const ExportListing = () => {
  const { messages } = useTypedIntl();

  const dispatch = useDispatch<AppDispatch>();
  const { exportList } = useSelector((state: AppState) => state.exportStore);
  const { loading, list, pagination, queryParams } = exportList;

  const [isWaitingExport, setIsWaitingExport] = useState(false);

  const { notificationList } = useSelector(
    (state: AppState) => state.notificationStore,
  );

  useEffect(() => {
    if (
      notificationList?.data?.[0]?.notificationType === 'EXPORT_COMPANY_DATA'
    ) {
      // A company export can take tens of minutes, so isWaitingExport (local
      // state) may be lost to a navigate/refresh/remount before it completes.
      // Refetch whenever an export-complete notification lands while the page
      // is open; only toast the user who triggered the export in this session.
      refetchData();
      if (isWaitingExport) {
        showMessage({
          type: 'success',
          content: 'Your files have been downloaded successfully',
        });
        setIsWaitingExport(false);
      }
    }
  }, [notificationList]);

  const [exportCompanyModal, setExportCompanyModal] = useState({
    open: false,
  });
  const [isExporting, setIsExporting] = useState<boolean>(false);

  useEffect(() => {
    handleFetchData({
      page: 0,
      size: 20,
    });
  }, []);

  const handleFetchData = (params: GetExportListParams) => {
    dispatch(exportActions.getExportList(params));
  };

  const refetchData = () => handleFetchData({ ...queryParams });

  const renderTitle = useCallback(() => {
    return (
      <AppPageTitle prefix={messages['export.title']} separator="" title={''} />
    );
  }, [messages]);

  const renderRightContent = () => {
    return (
      <div className="flex items-center gap-2">
        <AppButton
          type="primary"
          name={messages['common.export']}
          leftIcon={<Icons.IconArrowExport color="#fff" />}
          onClick={toggleExportCompany}
        />
      </div>
    );
  };

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const toggleExportCompany = () =>
    setExportCompanyModal({
      ...exportCompanyModal,
      open: !exportCompanyModal.open,
    });

  const handleExport = async (values: ExportCompanyParams) => {
    const params: ExportCompanyParams = {
      ids: values.ids,
      fileType: values.fileType,
      ...(values.fileType !== FILE_TYPE_VALUE.SUBCONTRACTOR
        ? {
            // "All Tax Periods" omits taxPeriod so the BE exports every period
            // of the chosen tax year (an absent taxPeriod = no period filter).
            ...(values.allTaxPeriod
              ? {}
              : { taxPeriod: toNumber(values.taxPeriod) }),
            taxYear: toNumber(values.taxYear),
          }
        : {}),
    };
    setIsExporting(true);
    await dispatch(exportActions.exportCompany(params));
    setIsWaitingExport(true);
    setIsExporting(false);
  };

  return (
    <AppsContainer
      fullView
      type="bottom"
      title={renderTitle()}
      rightContent={renderRightContent()}
      loading={isExporting}
    >
      <AppCard>
        <AppsContent>
          <AppTableContainer
            data={list ?? []}
            loading={loading}
            page={pagination.current}
            total={pagination.totalDocs}
            pageSize={pagination.pageSize}
            scroll={{ x: 1200, y: 700 }}
            titleEmpty={messages['export.empty']}
            columns={getColumns({
              messages,
            })}
            onChangePagination={onChangePagination}
            noDataType="noDataCreated"
            addNewBtnName={messages['common.export']}
            addNewBtnOnClick={toggleExportCompany}
            addNewIcon={null}
          />
        </AppsContent>
      </AppCard>
      <ExportCompanyModal
        isOpen={exportCompanyModal.open}
        toggle={toggleExportCompany}
        onExport={handleExport}
      />
    </AppsContainer>
  );
};

export default ExportListing;
